/**
 * BatchAmountModal Component
 *
 * Modal dialog for batch updating transaction amounts with validation and preview.
 * Validates positive numbers, displays real-time total calculation, and warns
 * when total amount exceeds R$ 1,000.00.
 *
 * @component
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const [selected, setSelected] = useState(new Set(['tx1', 'tx2', 'tx3']));
 *
 * <BatchAmountModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   selectedTransactions={selected}
 *   onClearSelection={() => setSelected(new Set())}
 * />
 * ```
 *
 * @remarks
 * - Only accepts positive decimal numbers
 * - Shows warning when total exceeds R$ 1,000.00
 * - Displays real-time preview of unit and total values
 * - Currency input with BRL formatting
 * - Automatically clears form after successful update
 *
 * @see {@link useBatchOperations.batchUpdateAmount} for the update logic
 */

import { useState } from 'react';
import { DollarSign } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useBatchOperations } from '@/hooks/useBatchOperations';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils/currencyUtils';

/**
 * Props for BatchAmountModal component
 *
 * @interface BatchAmountModalProps
 * @property {boolean} open - Controls modal visibility
 * @property {function} onOpenChange - Callback when modal open state changes
 * @property {Set<string>} selectedTransactions - Set of transaction IDs to update
 * @property {function} onClearSelection - Callback to clear selection after update
 */
export interface BatchAmountModalProps {
  /** Controls whether the modal is visible */
  open: boolean;
  /** Called when the modal should be opened or closed */
  onOpenChange: (open: boolean) => void;
  /** Set of transaction IDs to be updated */
  selectedTransactions: Set<string>;
  /** Called after successful update to clear the selection */
  onClearSelection: () => void;
}

export function BatchAmountModal({
  open,
  onOpenChange,
  selectedTransactions,
  onClearSelection,
}: BatchAmountModalProps) {
  const [amount, setAmount] = useState('');
  const { batchUpdateAmount, isUpdating } = useBatchOperations();

  const selectedIds = Array.from(selectedTransactions);
  const count = selectedIds.length;

  const handleConfirm = async () => {
    const numericAmount = parseFloat(amount);

    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Digite um valor válido maior que zero');
      return;
    }

    try {
      await batchUpdateAmount(selectedIds, numericAmount);
      onOpenChange(false);
      onClearSelection();

      // Reset form
      setAmount('');
    } catch (error) {
      // Error handling is done by the hook
      console.error('Batch amount update error:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setAmount('');
  };

  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.,]/g, '').replace(',', '.');
    return numericValue;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar valor em lote</DialogTitle>
          <DialogDescription>
            Defina um novo valor para {count} {count === 1 ? 'transação' : 'transações'}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="amount">
              Novo valor <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(formatCurrency(e.target.value))}
                disabled={isUpdating}
                className="pl-9"
              />
            </div>
          </div>

          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="text-muted-foreground">
              {count} {count === 1 ? 'transação será atualizada' : 'transações serão atualizadas'}
              {amount && parseFloat(amount) > 0 && (
                <>
                  <br />
                  <span className="font-medium">
                    Novo valor: R$ {parseFloat(amount).toFixed(2)}
                  </span>
                </>
              )}
            </p>
          </div>

          {amount && parseFloat(amount) * count > 1000 && (
            <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm">
              <p className="text-yellow-700 dark:text-yellow-400">
                ⚠️ Valor total: R$ {(parseFloat(amount) * count).toFixed(2)}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!amount || parseFloat(amount) <= 0 || isUpdating}
          >
            {isUpdating ? 'Atualizando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
